import React, { useEffect, useState } from "react";
import api from "@/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate, fmtTime, inr, STATUSES, PAYMENT_STATUSES, DELIVERY_PARTNERS, downloadCSV } from "@/lib/tps";
import { StatusBadge } from "@/components/StatusBadge";
import DatePicker from "@/components/DatePicker";
import { Plus, Search, Filter, X, Pencil, Trash2, Download, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import DeliveryConfirmModal from "@/components/DeliveryConfirmModal";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [delivery, setDelivery] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // "delete" | "status"
  const [bulkStatus, setBulkStatus] = useState("Confirmed");
  const [singleDelete, setSingleDelete] = useState(null); // order object

  const nav = useNavigate();
  const { isOwner } = useAuth();

  const fetchOrders = async () => {
    const params = new URLSearchParams();
    if (status) params.set("status_f", status);
    if (payment) params.set("payment_status", payment);
    if (delivery) params.set("delivery_method", delivery);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (q) params.set("q", q);
    const { data } = await api.get(`/orders?${params}`);
    setOrders(data);
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, [status, payment, delivery, start, end]);
  useEffect(() => {
    const t = setTimeout(fetchOrders, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const clear = () => { setStatus(""); setPayment(""); setDelivery(""); setStart(""); setEnd(""); setQ(""); };

  const allSelected = orders.length > 0 && orders.every((o) => selected.has(o.id));
  const clearSelection = () => setSelected(new Set());
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) clearSelection();
    else setSelected(new Set(orders.map((o) => o.id)));
  };

  const runBulkDelete = async () => {
    try {
      const { data } = await api.post("/orders/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} order${data.deleted !== 1 ? "s" : ""}. Customer records preserved.`);
      clearSelection(); setBulkAction(null); fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const runBulkStatus = async () => {
    // Intercept Delivered: if any selected order still has a positive balance,
    // ask whether to collect the balance for all of them.
    // NOTE: setBulkAction is deferred to the next tick because this is invoked from
    // an <AlertDialogAction> whose Radix close handler fires onOpenChange(false) in
    // the same event, which would otherwise reset bulkAction to null and swallow
    // our "delivered" state transition.
    if (bulkStatus === "Delivered") {
      const pending = orders.filter((o) => selected.has(o.id) && (o.balance_amount || 0) > 0);
      if (pending.length > 0) {
        setBulkAction(null);
        setTimeout(() => setBulkAction("delivered"), 0);
        return;
      }
    }
    try {
      const { data } = await api.post("/orders/bulk-status", { ids: Array.from(selected), status: bulkStatus });
      toast.success(`Updated ${data.updated} order${data.updated !== 1 ? "s" : ""} to ${bulkStatus}`);
      clearSelection(); setBulkAction(null); fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed");
    }
  };

  const runBulkDelivered = async (collect) => {
    const ids = Array.from(selected);
    let ok = 0, fail = 0;
    for (const oid of ids) {
      try { await api.post(`/orders/${oid}/mark-delivered`, { collect_balance: !!collect }); ok++; }
      catch { fail++; }
    }
    toast.success(`Marked ${ok} delivered${collect ? " (balances collected where pending)" : ""}${fail ? ` · ${fail} failed` : ""}`);
    clearSelection(); setBulkAction(null); fetchOrders();
  };

  const exportSelected = () => {
    const ids = new Set(selected);
    const chosen = orders.filter((o) => ids.has(o.id));
    if (chosen.length === 0) { toast.error("Select at least one order"); return; }
    const rows = chosen.map((o) => ({
      "Order ID": o.order_id,
      "Customer": o.customer_name,
      "Phone": o.customer_phone,
      "Instagram": o.customer_instagram || "",
      "Required Date": o.required_date,
      "Required Time": o.required_time,
      "Theme": o.items?.[0]?.theme || "",
      "Occasion": o.items?.[0]?.occasion || "",
      "Weight (kg)": o.items?.[0]?.weight || "",
      "Tier": o.items?.[0]?.tier || "",
      "Flavour": o.items?.[0]?.flavour || "",
      "Cake Message": o.items?.[0]?.cake_message || "",
      "Delivery Type": o.delivery_type || "",
      "Delivery Partner": o.delivery_method || "",
      "Delivery Address": o.delivery_address || "",
      "Google Maps Link": o.google_maps_link || "",
      "Total Price": o.total_price || 0,
      "Advance Paid": o.advance_amount || 0,
      "Balance Amount": o.balance_amount || 0,
      "Payment Status": o.payment_status || "",
      "Order Status": o.status,
    }));
    downloadCSV(`The_Palate_Stories_Orders_Selected_${format(new Date(), "yyyy-MM-dd")}.csv`, rows);
    toast.success(`Exported ${rows.length} order${rows.length !== 1 ? "s" : ""}`);
  };

  const confirmSingleDelete = async () => {
    try {
      await api.delete(`/orders/${singleDelete.id}`);
      toast.success("Order deleted. Customer record preserved.");
      setSingleDelete(null);
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-3 animate-fade-in" data-testid="orders-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">Orders</h1>
          <div className="text-xs text-[#4B5563]">
            {orders.length} shown{selected.size > 0 ? ` · ${selected.size} selected` : ""}
          </div>
        </div>
        {isOwner && (
          <Button onClick={() => nav("/orders/new")} className="bg-[#B86F77] hover:bg-[#A35D65] h-9" data-testid="orders-new-btn">
            <Plus className="h-4 w-4 mr-1" /> New Order
          </Button>
        )}
      </div>

      <Card className="border-[#E8E3DF]">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[#4B5563]" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, phone, order id"
                className="h-8 pl-8 border-[#E8E3DF]"
                data-testid="orders-search"
              />
            </div>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setShowFilters((s) => !s)} data-testid="orders-filter-toggle">
              <Filter className="h-3.5 w-3.5 mr-1" /> Filters
            </Button>
            {(status || payment || delivery || start || end) && (
              <Button variant="ghost" className="h-8" onClick={clear} data-testid="orders-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
              <FilterSelect value={status} onChange={setStatus} placeholder="Status" options={STATUSES} testId="filter-status" />
              <FilterSelect value={payment} onChange={setPayment} placeholder="Payment" options={PAYMENT_STATUSES} testId="filter-payment" />
              <FilterSelect value={delivery} onChange={setDelivery} placeholder="Delivery" options={DELIVERY_PARTNERS} testId="filter-delivery" />
              <DatePicker value={start} onChange={setStart} placeholder="Start date" testId="filter-start" />
              <DatePicker value={end} onChange={setEnd} placeholder="End date" testId="filter-end" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk actions bar */}
      {isOwner && selected.size > 0 && (
        <div className="bg-[#F3E8E9] border border-[#B86F77]/30 rounded-md p-2 flex items-center justify-between flex-wrap gap-2" data-testid="orders-bulk-bar">
          <div className="text-sm flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-[#B86F77]" />
            {selected.size} order{selected.size !== 1 ? "s" : ""} selected
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="h-8 w-40 bg-white border-[#E8E3DF]" data-testid="orders-bulk-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setBulkAction("status")} data-testid="orders-bulk-status-btn">
              Change Status
            </Button>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={exportSelected} data-testid="orders-bulk-export-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            <Button variant="outline" className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setBulkAction("delete")} data-testid="orders-bulk-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
            <Button variant="ghost" className="h-8" onClick={clearSelection} data-testid="orders-bulk-clear">Clear</Button>
          </div>
        </div>
      )}

      <Card className="border-[#E8E3DF] overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="tps-table">
            <TableHeader className="bg-[#FCF9F9]">
              <TableRow>
                {isOwner && (
                  <TableHead className="py-2 w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="orders-select-all-cb" aria-label="Select all orders" />
                  </TableHead>
                )}
                <TableHead className="py-2">Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Theme</TableHead>
                <TableHead>Delivery</TableHead>
                {isOwner && <TableHead className="text-right">Total</TableHead>}
                {isOwner && <TableHead className="text-right">Balance</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={isOwner ? 10 : 8} className="text-center text-[#4B5563] py-8">No orders found.</TableCell></TableRow>
              )}
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`order-row-${o.order_id}`}>
                  {isOwner && (
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(o.id)}
                        onCheckedChange={() => toggleOne(o.id)}
                        aria-label={`Select ${o.order_id}`}
                        data-testid={`order-select-cb-${o.order_id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="py-2 font-medium text-[#B86F77] cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{o.order_id}</TableCell>
                  <TableCell className="py-2 cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>
                    <div className="text-sm font-medium">{o.customer_name}</div>
                    <div className="text-[11px] text-[#4B5563]">{o.customer_phone}</div>
                  </TableCell>
                  <TableCell className="py-2 text-sm cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>
                    {fmtDate(o.required_date)}<br />
                    <span className="text-[11px] text-[#4B5563]">{fmtTime(o.required_time)}</span>
                  </TableCell>
                  <TableCell className="py-2 text-sm cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{o.items?.[0]?.theme || "—"}</TableCell>
                  <TableCell className="py-2 text-sm cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{o.delivery_method}</TableCell>
                  {isOwner && <TableCell className="py-2 text-right text-sm font-medium cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{inr(o.total_price)}</TableCell>}
                  {isOwner && <TableCell className="py-2 text-right text-sm cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{inr(o.balance_amount)}</TableCell>}
                  <TableCell className="py-2 cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => nav(`/orders/${o.id}/edit`)}
                        className="p-1 rounded hover:bg-[#F3E8E9] text-[#B86F77]"
                        title="Edit"
                        data-testid={`order-edit-${o.order_id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {isOwner && (
                        <button
                          onClick={() => setSingleDelete(o)}
                          className="p-1 rounded hover:bg-rose-50 text-rose-600"
                          title="Delete"
                          data-testid={`order-delete-${o.order_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Single delete confirm */}
      <AlertDialog open={!!singleDelete} onOpenChange={(v) => { if (!v) setSingleDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The customer record will NOT be deleted.
              {singleDelete && (<span className="block mt-2"><strong>{singleDelete.order_id}</strong> · {singleDelete.customer_name}</span>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSingleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="order-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkAction === "delete"} onOpenChange={(v) => { if (!v) setBulkAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} order{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Customer records are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="orders-bulk-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk status confirm */}
      <AlertDialog open={bulkAction === "status"} onOpenChange={(v) => { if (!v) setBulkAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change status to "{bulkStatus}" for {selected.size} order{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>Each order's status history will record this change.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkStatus} className="bg-[#B86F77] hover:bg-[#A35D65]" data-testid="orders-bulk-status-confirm">Update</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delivered — balance collection prompt */}
      <DeliveryConfirmModal
        open={bulkAction === "delivered"}
        balance={orders.filter((o) => selected.has(o.id)).reduce((s, o) => s + (o.balance_amount || 0), 0)}
        isOwner={isOwner}
        onCancel={() => setBulkAction(null)}
        onCollect={() => runBulkDelivered(true)}
        onDeliverOnly={() => runBulkDelivered(false)}
      />
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger className="h-8 border-[#E8E3DF] bg-white" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All {placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
